import type {
  MatchingImportWorkflowResult,
  MatchingParseError,
  MatchingRow,
} from "../types/matching";

/** File rows that failed client parse (exclude file-level nulls). */
export function matchingParseFailureFileRows(
  parseErrors: readonly MatchingParseError[]
): Set<number> {
  return new Set(
    parseErrors
      .map((e) => e.fileRow)
      .filter((r): r is number => r !== null)
  );
}

/** Rows that parsed cleanly and can still validate / import. */
export function matchingParsableRows(
  rows: readonly MatchingRow[],
  parseErrors: readonly MatchingParseError[]
): MatchingRow[] {
  const bad = matchingParseFailureFileRows(parseErrors);
  return rows.filter((r) => !bad.has(r.fileRow));
}

/**
 * Maps client parse errors onto MatchingRow indexes so MatchingImportReport
 * can render the CSV table with Fail chips — same shape as a validate result,
 * without calling the API.
 */
export function buildMatchingParseFailureReport(
  rows: readonly MatchingRow[],
  parseErrors: readonly MatchingParseError[]
): MatchingImportWorkflowResult {
  const messagesByFileRow = new Map<number, string[]>();
  for (const err of parseErrors) {
    if (err.fileRow == null) continue;
    const list = messagesByFileRow.get(err.fileRow) ?? [];
    list.push(err.message);
    messagesByFileRow.set(err.fileRow, list);
  }

  const errors = rows.flatMap((row, index) => {
    const messages = messagesByFileRow.get(row.fileRow);
    if (!messages?.length) return [];
    return [
      {
        index,
        employeeCode: row.employeeCode || null,
        field: null,
        error: messages.join("; "),
      },
    ];
  });

  // File-level messages (no fileRow) still need a home when every row also
  // failed — surface them on the first error row, otherwise leave them for
  // the alert banner only.
  const fileLevel = parseErrors
    .filter((e) => e.fileRow == null)
    .map((e) => e.message);
  if (fileLevel.length > 0 && errors.length > 0) {
    errors[0] = {
      ...errors[0],
      error: [...fileLevel, errors[0].error].join("; "),
    };
  }

  const failedIndexes = new Set(
    errors.map((e) => e.index).filter((i): i is number => i !== null)
  );

  return {
    total: rows.length,
    succeeded: Math.max(rows.length - failedIndexes.size, 0),
    failed: failedIndexes.size,
    errors,
    warnings: [],
  };
}

/**
 * Overlay a validate/commit result (indexed into `goodRows` only) onto the
 * full CSV row list so parse-failed stubs stay visible as errors while the
 * parsable subset shows API findings.
 */
export function mergeMatchingParseAndValidationReports(
  allRows: readonly MatchingRow[],
  parseErrors: readonly MatchingParseError[],
  goodRows: readonly MatchingRow[],
  validation: MatchingImportWorkflowResult
): MatchingImportWorkflowResult {
  const parseReport = buildMatchingParseFailureReport(allRows, parseErrors);

  const remapIndex = (goodIndex: number): number | null => {
    const fileRow = goodRows[goodIndex]?.fileRow;
    if (fileRow == null) return null;
    const at = allRows.findIndex((r) => r.fileRow === fileRow);
    return at >= 0 ? at : null;
  };

  const remappedErrors = validation.errors.map((e) => ({
    ...e,
    index: e.index !== null ? remapIndex(e.index) : null,
  }));
  const remappedWarnings = (validation.warnings ?? []).map((w) => ({
    ...w,
    indices: w.indices
      .map((i) => remapIndex(i))
      .filter((i): i is number => i !== null),
  }));
  const remappedResolved = (validation.resolvedRows ?? [])
    .map((r) => {
      const index = remapIndex(r.index);
      return index === null ? null : { ...r, index };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const errors = [...parseReport.errors, ...remappedErrors];

  // Preserve API outcome counts; parse-failure stubs add to failed only
  // (they contribute no successes — skipping / batch-level API totals stay intact).
  return {
    total: allRows.length,
    succeeded: validation.succeeded,
    failed: validation.failed + parseReport.failed,
    errors,
    warnings: remappedWarnings,
    resolvedRows: remappedResolved,
  };
}
